import React, { useState, useMemo } from 'react';
import { isObjectiveLockedByHR } from '../../services/pmsWorkflow';
import { motion } from 'framer-motion';
import {
  Objective,
  Milestone,
  Review,
  ReviewType,
  ReviewCycle,
  CreateObjectiveInput,
  CreateMilestoneInput,
  ObjectiveStatus,
} from '../../types/pms';
import GoalsTable from './GoalsTable';
import ReviewCycleSelector, { isHistoricalCycle } from './ReviewCycleSelector';
import RightSidePanel from './RightSidePanel';
import ObjectiveDetail from './ObjectiveDetail';
import ObjectiveForm from './ObjectiveForm';
import { PMSRole } from '../../types/pms';
import { submittableObjectives } from '../../services/pmsObjectiveState';
import { canEditSelfReview } from '../../services/pmsWorkflow';
import { getRatingLabel } from '../../services/pmsRatingCalculator';
import './EmployeeDashboard.css';
import './pms-premium.css';

interface EmployeeDashboardProps {
  employeeId: string;
  employeeName?: string;
  userRole: PMSRole;
  objectives: Objective[];
  milestonesByObjective: Record<string, Milestone[]>;
  reviews: Review[];
  reviewCycles: ReviewCycle[];
  /**
   * The child review cycles hanging off the selected objective cycle.
   *
   * Needed because "does the employee owe a self-review" depends on each child cycle's
   * reviewPhase, and because a review record does not exist until the employee first submits —
   * so the count cannot be derived from the `reviews` array alone.
   */
  childReviewCycles?: ReviewCycle[];
  selectedCycleId: string;
  onCycleChange: (cycleId: string) => void;
  onViewReview?: (review: Review) => void;
  onApproveObjective?: (objectiveId: string) => void;
  onReturnObjective?: (objectiveId: string, comments: string) => void;
  onRejectObjective?: (objectiveId: string, reason: string) => void;
  onEditObjective?: (objectiveId: string) => void;
  onAddMilestone?: (objectiveId: string) => void;
  onSendMessage?: (objectiveId: string, content: string) => void;
  onUploadAttachment?: (objectiveId: string, file: File) => void;
  onDownloadAttachment?: (attachment: any) => void;
  getVersions?: (objectiveId: string) => any[] | Promise<any[]>;
  getChatMessages?: (objectiveId: string) => any[] | Promise<any[]>;
  getAttachments?: (objectiveId: string) => any[] | Promise<any[]>;
  onCreateObjective?: () => void;
  onDeleteObjective?: (objectiveId: string) => void;
  onProgressChange?: (objectiveId: string, value: number) => void;
  onMilestoneProgressChange?: (objectiveId: string, milestoneId: string, value: number) => void;
  /** Save a single goal as draft (create or update) */
  onSaveGoal?: (input: CreateObjectiveInput, managerId: string, managerName: string, milestones: CreateMilestoneInput[]) => void | Promise<void>;
  /** Update an existing goal (edit flow, triggers re-approval if was approved) */
  onUpdateGoal?: (objectiveId: string, input: CreateObjectiveInput, milestones: CreateMilestoneInput[]) => void | Promise<void>;
  /** Submit ALL draft goals for approval at once */
  onSubmitAll?: () => void;
  reviewCycleIdForCreate?: string;
  defaultManagerId?: string;
  /** The manager's display name, passed straight to ObjectiveForm — see its prop docs. */
  defaultManagerName?: string;
}

const EmployeeDashboard: React.FC<EmployeeDashboardProps> = ({
  employeeId,
  employeeName,
  userRole,
  objectives,
  milestonesByObjective,
  reviews,
  reviewCycles,
  childReviewCycles = [],
  selectedCycleId,
  onCycleChange,
  onViewReview,
  onApproveObjective,
  onReturnObjective,
  onRejectObjective,
  onEditObjective,
  onAddMilestone,
  onSendMessage,
  onUploadAttachment,
  onDownloadAttachment,
  getVersions,
  getChatMessages,
  getAttachments,
  onCreateObjective,
  onDeleteObjective,
  onProgressChange,
  onMilestoneProgressChange,
  onSaveGoal,
  onUpdateGoal,
  onSubmitAll,
  reviewCycleIdForCreate,
  defaultManagerId = '',
  defaultManagerName = '',
}) => {
  const [selectedObjective, setSelectedObjective] = useState<Objective | null>(null);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  /*
   * Bumped every time the objective panel is opened, and used as ObjectiveForm's key.
   *
   * RightSidePanel renders its children unconditionally — `isOpen` only toggles a CSS class
   * for the slide animation — so ObjectiveForm mounts once and stays mounted for the life of
   * this dashboard. Every field in it is seeded with `useState(objective?.x ?? '')`, which is
   * an *initial* value: React evaluates it on the first render and never again. Clicking Edit
   * changed the props but not the state, so the form opened blank (or still holding the last
   * thing typed) instead of showing the saved objective — title, dates, weightage, track-by,
   * comments and milestones alike. Keying on the open count forces a fresh mount per opening,
   * so the initialisers re-run against the objective actually being edited. Deliberately not
   * `key={objective.id}`: that would not remount when re-opening the *same* objective, which
   * is exactly when abandoned edits would linger.
   */
  const [formSession, setFormSession] = useState(0);
  const [editingObjective, setEditingObjective] = useState<Objective | null>(null);
  const [detailVersions, setDetailVersions] = useState<any[]>([]);
  const [detailChat, setDetailChat] = useState<any[]>([]);
  const [detailAttachments, setDetailAttachments] = useState<any[]>([]);

  const isHistorical = useMemo(
    () => isHistoricalCycle(reviewCycles, selectedCycleId),
    [reviewCycles, selectedCycleId]
  );

  const isCycleLocked = useMemo(() => {
    const selectedCycle = reviewCycles.find(c => c.id === selectedCycleId);
    // isObjectiveLockedByHR with no objective answers "is the cycle itself locked", and checks
    // status === Locked as well as the isLocked flag — lockPMSObjectives writes both.
    return isObjectiveLockedByHR(undefined, selectedCycle);
  }, [reviewCycles, selectedCycleId]);

  const isReadOnly = isHistorical || isCycleLocked;

  const visibleObjectives = useMemo(
    () => objectives.filter((objective) =>
      String((objective as any).objectiveCycleId || objective.reviewCycleId || '') === selectedCycleId
    ),
    [objectives, selectedCycleId]
  );

  const overallProgress = useMemo(() => {
    if (visibleObjectives.length === 0) return 0;
    const totalW = visibleObjectives.reduce((sum, o) => sum + o.weightage, 0);
    if (totalW === 0) return 0;
    return Math.round(visibleObjectives.reduce((sum, o) => sum + o.progressPercentage * o.weightage, 0) / totalW);
  }, [visibleObjectives]);

  const totalWeightage = useMemo(
    () => visibleObjectives.reduce((sum, o) => sum + o.weightage, 0),
    [visibleObjectives]
  );

  const existingWeightageTotal = useMemo(() => {
    if (editingObjective) {
      return visibleObjectives.filter(o => o.id !== editingObjective.id).reduce((sum, o) => sum + o.weightage, 0);
    }
    return totalWeightage;
  }, [visibleObjectives, editingObjective, totalWeightage]);

  /**
   * The objectives this bar would actually send — drafts AND returned ones.
   *
   * This read `status === DRAFT`, and a RETURNED objective is not a DRAFT, so the bar did not
   * render for an employee whose only outstanding work was something their manager had sent
   * back. They could read the comment and edit the goal, and then had nowhere to submit it: it
   * could not be approved and could not be withdrawn. submittableObjectives is the shared
   * answer, so this and handleSubmitAll cannot disagree about what "ready" means again.
   */
  const draftObjectives = useMemo(
    () => submittableObjectives(visibleObjectives),
    [visibleObjectives]
  );

  /** Whether any of them is a resubmission, which changes what the bar says. */
  const hasReturned = useMemo(
    () => draftObjectives.some(o => o.status === ObjectiveStatus.RETURNED),
    [draftObjectives]
  );

  // Show "Submit All" when: there are draft goals, weightage = 100, and at least 1 goal exists
  const canSubmitAll = draftObjectives.length > 0 && totalWeightage === 100;

  // Show a warning message when weightage != 100 but there are drafts
  const weightageMessage = useMemo(() => {
    if (draftObjectives.length === 0) return null;
    if (totalWeightage < 100) return `Add more goals to reach 100% weightage (currently ${totalWeightage}%)`;
    if (totalWeightage > 100) return `Total weightage exceeds 100% (currently ${totalWeightage}%). Please adjust.`;
    return null;
  }, [draftObjectives, totalWeightage]);

  /*
   * Reviews for the selected objective cycle — those filed against any of its CHILD review
   * cycles. Reviews are never filed against the annual cycle itself, so comparing a review's
   * child-cycle id to the annual id (as this did) matched nothing and left every review-derived
   * metric on this page permanently empty. The parent id stays in the set for older records
   * written directly against an annual cycle.
   */
  const cycleReviews = useMemo(() => {
    const ids = new Set([selectedCycleId, ...childReviewCycles.map((c) => c.id)]);
    return reviews.filter((r) => ids.has(r.reviewCycleId));
  }, [reviews, selectedCycleId, childReviewCycles]);

  const finalReview = useMemo(
    () => cycleReviews.find((r) => r.reviewType === ReviewType.END_YEAR),
    [cycleReviews]
  );

  const selectedCycle = useMemo(
    () => reviewCycles.find((c) => c.id === selectedCycleId),
    [reviewCycles, selectedCycleId]
  );

  /*
   * Reviews that need something from the EMPLOYEE — not reviews that merely exist.
   *
   * Two things can be owed and both are counted: a self-review not yet submitted on a cycle HR
   * has opened (canEditSelfReview — the same rule that decides whether My Reviews offers the
   * Start Self Review button, so the count and the button cannot disagree), and a
   * manager-completed review not yet acknowledged.
   *
   * Iterating the CHILD CYCLES rather than the reviews array is essential to the first case:
   * submitSelfReview creates the Review row lazily on first submit, so in the window where the
   * employee most needs telling, there is no record to count.
   */
  const pendingReviews = useMemo(() => {
    const reviewFor = (cycleId: string) => cycleReviews.find((r) => r.reviewCycleId === cycleId);

    const owedSelfReviews = childReviewCycles.filter((cycle) =>
      canEditSelfReview(cycle, reviewFor(cycle.id))
    ).length;

    const owedAcknowledgements = cycleReviews.filter(
      (r) => r.managerSubmittedAt && !r.isAcknowledged
    ).length;

    return owedSelfReviews + owedAcknowledgements;
  }, [childReviewCycles, cycleReviews]);

  const cycleOverviewTitle = useMemo(() => {
    const cycleName = selectedCycle?.name?.trim();
    return cycleName || 'Current objective cycle';
  }, [selectedCycle]);

  const heroMotion = {
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
  };

  const handleObjectiveClick = async (objective: Objective) => {
    setSelectedObjective(objective);
    // Load detail data (supports both sync and async)
    if (getVersions) {
      const v = getVersions(objective.id);
      setDetailVersions(v instanceof Promise ? await v : v);
    }
    if (getChatMessages) {
      const c = getChatMessages(objective.id);
      setDetailChat(c instanceof Promise ? await c : c);
    }
    if (getAttachments) {
      const a = getAttachments(objective.id);
      setDetailAttachments(a instanceof Promise ? await a : a);
    }
  };

  const handleCreateClick = () => {
    if (onSaveGoal) {
      setFormSession(n => n + 1);
      setShowCreatePanel(true);
    } else if (onCreateObjective) {
      onCreateObjective();
    }
  };

  const handleEditClick = (objective: Objective) => {
    if (onSaveGoal || onUpdateGoal) {
      setEditingObjective(objective);
      setFormSession(n => n + 1);
      setShowCreatePanel(true);
    }
  };

  const handleCloseCreatePanel = () => {
    setShowCreatePanel(false);
    setEditingObjective(null);
  };

  return (
    <div className="pms-employee-dashboard">
      <div className="pms-ed-header">
        <div className="pms-ed-header__left">
          <h1 className="pms-ed-header__title">{employeeName ? `${employeeName}'s Objectives` : 'My Objectives'}</h1>
          <p className="pms-ed-header__subtitle">Track your goals, milestones, and performance progress</p>
        </div>
        <div className="pms-ed-header__right">
          {!isReadOnly && reviewCycleIdForCreate && (onCreateObjective || onSaveGoal) && (
            <button type="button" className="pms-btn pms-btn-primary" onClick={handleCreateClick}>+ New Objective</button>
          )}
          <ReviewCycleSelector
            cycles={reviewCycles}
            selectedCycleId={selectedCycleId}
            onChange={onCycleChange}
            label="Objective Cycle"
            emptyLabel="No Active Objective Cycle Available"
          />
        </div>
      </div>

      {isHistorical && (
        <div className="pms-ed-banner pms-ed-banner--historical" role="status">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
          You are viewing a previous review cycle. All data is read-only.
        </div>
      )}

      {isCycleLocked && !isHistorical && (
        <div className="pms-ed-banner pms-ed-banner--historical" role="status">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          This objective cycle is locked. Objectives and reviews cannot be modified until HR unlocks the cycle.
        </div>
      )}

      {!reviewCycleIdForCreate && !isReadOnly && visibleObjectives.length === 0 && (
        <div className="pms-ed-banner pms-ed-banner--info" role="status">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          <strong>No Active Objective Cycle Available</strong>
          <span>Objectives can be created once HR activates an objective cycle.</span>
        </div>
      )}

      <motion.section className="pms-ed-hero" {...heroMotion}>
        <div className="pms-ed-hero__content">
          <div className="pms-ed-hero__top-row">
            <div>
              <span className="pms-ed-hero__eyebrow">Objective Cycle</span>
              <h2 className="pms-ed-hero__cycle-title">{cycleOverviewTitle}</h2>
            </div>
            {finalReview?.overallRating && onViewReview && (
              <button type="button" className="pms-ed-hero__cta" onClick={() => onViewReview(finalReview)}>
                View Review Summary
              </button>
            )}
          </div>

          <div className="pms-ed-kpi-row" aria-label="Objective summary">
            <div className="pms-ed-kpi pms-ed-kpi--progress">
              <span className="pms-ed-kpi__label">Overall Progress</span>
              <strong className="pms-ed-kpi__value">{overallProgress}%</strong>
              <div
                className="pms-ed-kpi__progress-track"
                role="progressbar"
                aria-label="Overall progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={overallProgress}
              >
                <span className="pms-ed-kpi__progress-fill" style={{ width: `${overallProgress}%` }} />
              </div>
            </div>

            <div className="pms-ed-kpi">
              <span className="pms-ed-kpi__label">Objectives Count</span>
              <strong className="pms-ed-kpi__value">{visibleObjectives.length}</strong>
            </div>
            <div className="pms-ed-kpi">
              <span className="pms-ed-kpi__label">Weightage</span>
              <strong className={`pms-ed-kpi__value ${totalWeightage === 100 ? '' : 'pms-ed-kpi__value--alert'}`}>{totalWeightage}%</strong>
            </div>
            <div className="pms-ed-kpi">
              <span className="pms-ed-kpi__label">Pending Reviews</span>
              <strong className={`pms-ed-kpi__value ${pendingReviews > 0 ? 'pms-ed-kpi__value--alert' : ''}`}>{pendingReviews}</strong>
            </div>
            <div className="pms-ed-kpi">
              <span className="pms-ed-kpi__label">Final Rating</span>
              <strong className="pms-ed-kpi__value pms-ed-kpi__value--rating">
                {finalReview?.overallRating ? getRatingLabel(finalReview.overallRating) : 'Not Rated Yet'}
              </strong>
            </div>
          </div>
        </div>
      </motion.section>

      <motion.div
        className="pms-ed-objectives-section"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.08 }}
      >
      <GoalsTable
        objectives={visibleObjectives}
        milestonesByObjective={milestonesByObjective}
        onObjectiveClick={handleObjectiveClick}
        onDeleteObjective={!isReadOnly ? onDeleteObjective : undefined}
        onEditObjective={!isReadOnly && (onSaveGoal || onUpdateGoal) ? handleEditClick : undefined}
        onProgressChange={!isReadOnly ? onProgressChange : undefined}
        onMilestoneProgressChange={!isReadOnly ? onMilestoneProgressChange : undefined}
        isReadOnly={isReadOnly}
        cycleLocked={isCycleLocked}
        hideCompletionSummary
      />
      </motion.div>

      {/* Submit All for Approval — bottom bar */}
      {!isReadOnly && onSubmitAll && draftObjectives.length > 0 && (
        <div className="pms-ed-submit-bar">
          <div className="pms-ed-submit-bar__info">
            <span className="pms-ed-submit-bar__count">
              {/* Named for what they are: "draft" is wrong for a goal the manager sent back. */}
              {draftObjectives.length} goal{draftObjectives.length > 1 ? 's' : ''} ready
              {hasReturned ? ' (including returned)' : ''}
            </span>
            {weightageMessage && (
              <span className="pms-ed-submit-bar__warn">{weightageMessage}</span>
            )}
            {canSubmitAll && (
              <span className="pms-ed-submit-bar__ready">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Weightage complete — ready to submit
              </span>
            )}
          </div>
          <button
            type="button"
            className="pms-btn pms-btn-submit-all"
            onClick={onSubmitAll}
            disabled={!canSubmitAll}
            title={!canSubmitAll ? 'Total goal weightage must equal 100% before submitting' : 'Send these goals to your manager for approval'}
          >
            {hasReturned ? 'Resubmit for Approval' : 'Submit All for Approval'} ({draftObjectives.length})
          </button>
        </div>
      )}

      {/* Objective Detail Panel */}
      <RightSidePanel
        isOpen={!!selectedObjective && !showCreatePanel}
        title={selectedObjective?.title || 'Objective Detail'}
        onClose={() => setSelectedObjective(null)}
      >
        {selectedObjective && (
          <ObjectiveDetail
            objective={selectedObjective}
            milestones={milestonesByObjective[selectedObjective.id] || []}
            versions={detailVersions}
            chatMessages={detailChat}
            attachments={detailAttachments}
            userRole={userRole}
            userId={employeeId}
            isReadOnly={isReadOnly}
            onApprove={!isReadOnly && onApproveObjective ? () => onApproveObjective(selectedObjective.id) : undefined}
            onReturn={!isReadOnly && onReturnObjective ? (c) => onReturnObjective(selectedObjective.id, c) : undefined}
            onReject={!isReadOnly && onRejectObjective ? (r) => onRejectObjective(selectedObjective.id, r) : undefined}
            onEdit={!isReadOnly && onEditObjective ? () => onEditObjective(selectedObjective.id) : undefined}
            onAddMilestone={!isReadOnly && onAddMilestone ? () => onAddMilestone(selectedObjective.id) : undefined}
            onSendMessage={!isReadOnly && onSendMessage ? (c) => onSendMessage(selectedObjective.id, c) : undefined}
            onUploadAttachment={!isReadOnly && onUploadAttachment ? (f) => onUploadAttachment(selectedObjective.id, f) : undefined}
            onDownloadAttachment={onDownloadAttachment}
            onProgressChange={!isReadOnly && onProgressChange ? (value) => onProgressChange(selectedObjective.id, value) : undefined}
          />
        )}
      </RightSidePanel>

      {/* Create/Edit Objective Panel */}
      {(onSaveGoal || onUpdateGoal) && (
        <RightSidePanel
          isOpen={showCreatePanel}
          title={editingObjective ? 'Edit Objective' : 'New Objective'}
          onClose={handleCloseCreatePanel}
          wide
        >
          <ObjectiveForm
            key={formSession}
            objective={editingObjective ?? undefined}
            existingWeightageTotal={existingWeightageTotal}
            existingMilestones={editingObjective ? milestonesByObjective[editingObjective.id] : undefined}
            reviewCycleId={reviewCycleIdForCreate}
            defaultManagerId={defaultManagerId}
            defaultManagerName={defaultManagerName}
            onSave={async (input, mId, mName, ms) => {
              if (editingObjective && onUpdateGoal) {
                await onUpdateGoal(editingObjective.id, input, ms);
              } else if (onSaveGoal) {
                await onSaveGoal(input, mId, mName, ms);
              }
              /*
               * Closed only once the save resolved. It used to close immediately and
               * unconditionally, so a rejected save took the panel — and everything typed
               * into it — away with it, leaving nothing on screen to say what happened.
               */
              handleCloseCreatePanel();
            }}
            onCancel={handleCloseCreatePanel}
          />
        </RightSidePanel>
      )}
    </div>
  );
};

export default EmployeeDashboard;
